import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, Plus, Edit2, Trash2, AlertTriangle, Package } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayStr = () => new Date().toISOString().split('T')[0];

const ConfigurationPage = () => {
  const { getAuthHeaders } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [receptionDialogOpen, setReceptionDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);

  const [formData, setFormData] = useState({
    name: '', type: '', unit: 'tonne', price_per_unit: '', current_stock: '', alert_threshold: ''
  });

  const [receptionForm, setReceptionForm] = useState({
    material_id: '', tonnage: '', price_per_tonne: '', supplier: '', date: todayStr()
  });

  const fetchData = useCallback(async () => {
    try {
      const [matRes, recRes] = await Promise.all([
        axios.get(`${API}/raw-materials`, getAuthHeaders()),
        axios.get(`${API}/material-receptions?limit=50`, getAuthHeaders())
      ]);
      setMaterials(matRes.data);
      setReceptions(recRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReceptionSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/material-receptions`, {
        material_id: receptionForm.material_id,
        tonnage: parseFloat(receptionForm.tonnage),
        price_per_tonne: receptionForm.price_per_tonne ? parseFloat(receptionForm.price_per_tonne) : null,
        supplier: receptionForm.supplier || null,
        date: receptionForm.date ? `${receptionForm.date}T12:00:00` : null
      }, getAuthHeaders());
      toast.success('Camion réceptionné, stock mis à jour');
      setReceptionDialogOpen(false);
      setReceptionForm({ material_id: '', tonnage: '', price_per_tonne: '', supplier: '', date: todayStr() });
      fetchData();
    } catch (error) {
      console.error('Error saving reception:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteReception = async (id) => {
    if (!window.confirm('Supprimer cette réception ? Le stock sera réajusté.')) return;
    try {
      await axios.delete(`${API}/material-receptions/${id}`, getAuthHeaders());
      toast.success('Réception supprimée, stock ajusté');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        unit: formData.unit,
        price_per_unit: parseFloat(formData.price_per_unit),
        current_stock: parseFloat(formData.current_stock),
        alert_threshold: parseFloat(formData.alert_threshold)
      };
      if (editingMaterial) {
        await axios.put(`${API}/raw-materials/${editingMaterial.id}`, payload, getAuthHeaders());
        toast.success('Matière première mise à jour');
      } else {
        await axios.post(`${API}/raw-materials`, payload, getAuthHeaders());
        toast.success('Matière première ajoutée');
      }
      fetchData();
      setMaterialDialogOpen(false);
      resetMaterialForm();
    } catch (error) {
      console.error('Error saving material:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const resetMaterialForm = () => {
    setFormData({ name: '', type: '', unit: 'tonne', price_per_unit: '', current_stock: '', alert_threshold: '' });
    setEditingMaterial(null);
  };

  const openEditDialog = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      type: material.type,
      unit: material.unit,
      price_per_unit: material.price_per_unit.toString(),
      current_stock: material.current_stock.toString(),
      alert_threshold: material.alert_threshold.toString()
    });
    setMaterialDialogOpen(true);
  };

  const openReceptionDialog = () => {
    setReceptionForm({ material_id: '', tonnage: '', price_per_tonne: '', supplier: '', date: todayStr() });
    setReceptionDialogOpen(true);
  };

  const onReceptionMaterialChange = (materialId) => {
    const mat = materials.find(m => m.id === materialId);
    setReceptionForm(prev => ({
      ...prev,
      material_id: materialId,
      price_per_tonne: mat ? mat.price_per_unit.toString() : prev.price_per_tonne
    }));
  };

  const formatNumber = (n) => Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR');

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="configuration-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Suivi Matières Premières
          </h1>
          <p className="text-sm text-slate-600 mt-1">Réceptionnez vos camions ou créez un achat — le stock s'alimente automatiquement et se déduit selon la production</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={openReceptionDialog}
            className="bg-[#FDC800] hover:bg-[#e6b600] text-[#1B3268] font-bold rounded-sm shadow-sm"
            data-testid="add-reception-button"
          >
            <Truck className="w-4 h-4 mr-2" />
            Réception Camion
          </Button>
          <Button
            variant="outline"
            onClick={() => { resetMaterialForm(); setMaterialDialogOpen(true); }}
            className="border-slate-300 text-[#1B3268] rounded-sm"
            data-testid="add-material-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Matière
          </Button>
        </div>
      </div>

      {/* Stock cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {materials.map((material) => {
          const lowStock = material.current_stock <= material.alert_threshold;
          return (
            <Card
              key={material.id}
              className={`bg-white border rounded-sm shadow-sm p-5 relative ${lowStock ? 'border-red-300' : 'border-slate-200'}`}
              data-testid={`material-card-${material.type}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-400" />
                  <span className="font-semibold text-[#1B3268]">{material.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(material)}
                  data-testid={`edit-material-${material.type}`}
                  className="hover:bg-slate-100 text-slate-500 rounded-sm h-7 w-7 p-0"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="mt-3">
                <span className={`text-3xl font-bold font-mono ${lowStock ? 'text-red-600' : 'text-slate-900'}`} data-testid={`stock-value-${material.type}`}>
                  {formatNumber(material.current_stock)}
                </span>
                <span className="text-sm text-slate-500 ml-1">{material.unit}{material.current_stock > 1 && material.unit === 'tonne' ? 's' : ''}</span>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Prix actuel : <span className="font-mono font-medium text-slate-700">{formatNumber(material.price_per_unit)} FCFA/{material.unit}</span>
              </div>
              {lowStock && (
                <div className="mt-2 flex items-center gap-1 text-xs text-red-600 font-medium" data-testid={`low-stock-alert-${material.type}`}>
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Stock bas (seuil : {formatNumber(material.alert_threshold)})
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Receptions history */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="receptions-table">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Truck className="w-4 h-4 text-[#1B3268]" />
          <h2 className="text-base font-semibold text-[#1B3268]">Historique des réceptions (camions)</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Matériau</th>
                <th className="p-3 text-right">Tonnage</th>
                <th className="p-3 text-right">Prix/tonne</th>
                <th className="p-3 text-right">Montant</th>
                <th className="p-3 text-left">Fournisseur</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {receptions.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-500 text-sm" data-testid="no-receptions-message">
                    Aucune réception enregistrée. Cliquez sur "Réception Camion" pour ajouter votre premier camion.
                  </td>
                </tr>
              )}
              {receptions.map((rec) => (
                <tr key={rec.id} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`reception-row-${rec.id}`}>
                  <td className="p-3 font-mono text-sm">{formatDate(rec.date)}</td>
                  <td className="p-3 font-medium">{rec.material_name}</td>
                  <td className="p-3 text-right font-mono">{formatNumber(rec.tonnage)} t</td>
                  <td className="p-3 text-right font-mono">{rec.price_per_tonne ? `${formatNumber(rec.price_per_tonne)} FCFA` : '—'}</td>
                  <td className="p-3 text-right font-mono font-medium">{rec.price_per_tonne ? `${formatNumber(rec.tonnage * rec.price_per_tonne)} FCFA` : '—'}</td>
                  <td className="p-3 text-slate-600">{rec.supplier || '—'}</td>
                  <td className="p-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteReception(rec.id)}
                      data-testid={`delete-reception-${rec.id}`}
                      className="hover:bg-red-50 text-red-500 rounded-sm h-7 w-7 p-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Reception Dialog */}
      <Dialog open={receptionDialogOpen} onOpenChange={setReceptionDialogOpen}>
        <DialogContent className="bg-white" data-testid="reception-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B3268] flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <Truck className="w-5 h-5" />
              Réception Camion
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Enregistrez un camion reçu : le stock du matériau augmente automatiquement.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReceptionSubmit} className="space-y-4">
            <div>
              <Label>Matériau *</Label>
              <Select value={receptionForm.material_id} onValueChange={onReceptionMaterialChange} required>
                <SelectTrigger data-testid="reception-material-select" className="h-10 border-slate-300 rounded-sm mt-1">
                  <SelectValue placeholder="Choisir un matériau" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {materials.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`reception-material-option-${m.type}`}>
                      {m.name} (stock : {formatNumber(m.current_stock)} {m.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tonnage reçu (t) *</Label>
                <Input
                  type="number" step="0.01" min="0.01" required
                  value={receptionForm.tonnage}
                  onChange={(e) => setReceptionForm({ ...receptionForm, tonnage: e.target.value })}
                  placeholder="Ex: 30"
                  data-testid="reception-tonnage-input"
                  className="h-10 border-slate-300 rounded-sm mt-1"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={receptionForm.date}
                  onChange={(e) => setReceptionForm({ ...receptionForm, date: e.target.value })}
                  data-testid="reception-date-input"
                  className="h-10 border-slate-300 rounded-sm mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prix/tonne (FCFA)</Label>
                <Input
                  type="number" step="0.01"
                  value={receptionForm.price_per_tonne}
                  onChange={(e) => setReceptionForm({ ...receptionForm, price_per_tonne: e.target.value })}
                  placeholder="Optionnel"
                  data-testid="reception-price-input"
                  className="h-10 border-slate-300 rounded-sm mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Met à jour le prix du matériau</p>
              </div>
              <div>
                <Label>Fournisseur</Label>
                <Input
                  value={receptionForm.supplier}
                  onChange={(e) => setReceptionForm({ ...receptionForm, supplier: e.target.value })}
                  placeholder="Optionnel"
                  data-testid="reception-supplier-input"
                  className="h-10 border-slate-300 rounded-sm mt-1"
                />
              </div>
            </div>

            {receptionForm.tonnage && receptionForm.price_per_tonne && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm" data-testid="reception-total-preview">
                Montant du camion : <span className="font-mono font-bold text-[#1B3268]">
                  {formatNumber(parseFloat(receptionForm.tonnage || 0) * parseFloat(receptionForm.price_per_tonne || 0))} FCFA
                </span>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                disabled={!receptionForm.material_id}
                data-testid="reception-submit-button"
                className="flex-1 bg-[#1B3268] hover:bg-[#152a55] text-white font-medium rounded-sm shadow-sm transition-all active:scale-95"
              >
                Enregistrer la réception
              </Button>
              <Button type="button" variant="outline" onClick={() => setReceptionDialogOpen(false)} className="flex-1 rounded-sm">
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Material Dialog */}
      <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
        <DialogContent className="bg-white" data-testid="material-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B3268]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {editingMaterial ? 'Modifier Matière Première' : 'Nouvelle Matière Première'}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Définissez le nom, le prix et le seuil d'alerte du matériau.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMaterialSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nom</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="material-name-input"
                className="h-10 border-slate-300 rounded-sm mt-1"
              />
            </div>

            <div>
              <Label htmlFor="type">Type</Label>
              <Input
                id="type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
                placeholder="Ex: ciment_42_5, sable_0_8, gravier_3_8, gravier_2_4"
                data-testid="material-type-input"
                className="h-10 border-slate-300 rounded-sm mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="unit">Unité</Label>
                <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                  <SelectTrigger data-testid="material-unit-select" className="h-10 border-slate-300 rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="tonne">Tonne (t)</SelectItem>
                    <SelectItem value="kg">Kilogramme (kg)</SelectItem>
                    <SelectItem value="sac">Sac</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="price">Prix Unitaire (FCFA)</Label>
                <Input
                  id="price" type="number" step="0.01" required
                  value={formData.price_per_unit}
                  onChange={(e) => setFormData({ ...formData, price_per_unit: e.target.value })}
                  data-testid="material-price-input"
                  className="h-10 border-slate-300 rounded-sm mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="stock">Stock Actuel</Label>
                <Input
                  id="stock" type="number" step="0.01" required
                  value={formData.current_stock}
                  onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                  data-testid="material-stock-input"
                  className="h-10 border-slate-300 rounded-sm mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Ajustement manuel (inventaire)</p>
              </div>
              <div>
                <Label htmlFor="threshold">Seuil d'Alerte</Label>
                <Input
                  id="threshold" type="number" step="0.01" required
                  value={formData.alert_threshold}
                  onChange={(e) => setFormData({ ...formData, alert_threshold: e.target.value })}
                  data-testid="material-threshold-input"
                  className="h-10 border-slate-300 rounded-sm mt-1"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                data-testid="material-submit-button"
                className="flex-1 bg-[#1B3268] hover:bg-[#152a55] text-white font-medium rounded-sm shadow-sm transition-all active:scale-95"
              >
                {editingMaterial ? 'Mettre à jour' : 'Ajouter'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setMaterialDialogOpen(false)} className="flex-1 rounded-sm">
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigurationPage;
